import UserAvatar from './UserAvatar';
import React from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import type { Session } from '@supabase/supabase-js';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import useSessionStore, { AssignedRegion, SessionProfile } from '../state/session';
import { getSiteAccessState } from '../state/siteAccess';
import useActiveLocationStore from '../state/locationScope';
import { useRoles, usePersistImpersonation } from '../hooks/useRoles';
import { useInstallationAccess } from '../hooks/useInstallationAccess';
import '../global.css';
import './appearance.css';
import ToastProvider from './ToastProvider';
import AppUpdateBanner from './AppUpdateBanner';
import Footer from '../components/Footer';
import FocusRemountGuard from './FocusRemountGuard';
import DeferMount from './DeferMount';
import { installPerfGuards } from '../lib/perfGuards';
import { installNetTap } from '../lib/netTap';
import SuperAdminHoverInspector from '../components/SuperAdminHoverInspector';
import DiagBoot from './DiagBoot';
import { useUserPrefs } from '../hooks/useUserPrefs';
import { useDeviceDarkMode } from '../hooks/useDeviceDarkMode';
import { appearanceModeFor, appearanceToneFor, paletteFor, type AppearanceMode, type AppearanceTone } from '../domain/appearance';
import { useGlobalAutoFlush } from '../hooks/useGlobalAutoFlush';
import { useAppPresence } from '../hooks/useAppPresence';
import { applyShellThemeAttributes, readShellThemeCache, writeShellThemeCache } from './shellTheme';
import { useScheduledHolidayTheme } from './useHolidayTheme';
import { holidayThemeLabel, isAvailableHolidayTheme, resolveShellTheme, type HolidayThemeKey } from '../domain/holidayThemes';
import MessageGuard from './MessageGuard';
import OptionalDecorationBoundary from './OptionalDecorationBoundary';
import { getSupabaseClient } from '../data/supabase';
import { fetchLocations } from '../data/locations';
import { refreshSessionContext, updateProfileById } from '../data/userProfiles';
import { getEventsNavTarget } from './appShellNavigation';
import SimpleInOutHeaderStatus from '../components/integrations/SimpleInOutHeaderStatus';
import SimpleInOutAccountMenuSection from '../components/integrations/SimpleInOutAccountMenuSection';
import SimpleInOutOverlays from '../components/integrations/SimpleInOutOverlays';
import AdaptivePrimaryNav, { type PrimaryNavItem } from './AdaptivePrimaryNav';
import { NO_PAGE_FEATURES, TEST_LAYOUT_OPTIONS, getTestLayout, isTestLayoutId, type PageKit, type TestLayoutId, type TestLayoutOption } from './layouts/registry';
import { PUBLIC_SHELL_LAYOUT, ShellLayoutContext, type ShellLayoutValue } from './ShellLayoutContext';
import LayoutFeature from './LayoutFeature';
import { usePageTitleContext } from './pageTitle';
import type { RouteBreadcrumbProps } from '../components/shell/RouteBreadcrumb';
import { navIcon } from './navIcons';
import useMediaQuery from '../hooks/useMediaQuery';
import type { ShellBottomNavItem } from '../components/shell/ShellBottomNav';
import type { CommandPaletteItem, CommandPaletteSearch } from '../components/shell/CommandPalette';
import { RECORD_SEARCH_MIN_LENGTH, searchRecords } from '@/data/recordSearch';
import { requestPrincipalLogout } from '../auth/sessionSignals';
import { isPublicTokenPath } from '../auth/routePolicy';
import { runSupabaseQuery, safeErrorMessage } from '../utils/appErrors';

const ChatWidget = React.lazy(() => import('../components/ChatWidget'));
const HolidayTopbarDecor = React.lazy(() => import('../components/holidays/HolidayTopbarDecor'));
const HolidayLogoMark = React.lazy(() => import('../components/holidays/HolidayTopbarDecor').then((module) => ({ default: module.HolidayLogoMark })));
const OrderChatMentionOverlay = React.lazy(() => import('../components/order-chat/OrderChatMentionOverlay'));
const NotificationBell = React.lazy(() => import('../components/NotificationBell'));
// Test layouts only (docs/test-layouts.md): nobody else downloads these.
const CommandPalette = React.lazy(() => import('../components/shell/CommandPalette'));
const CommandStrip = React.lazy(() => import('../components/shell/CommandStrip'));
const ShellBottomNav = React.lazy(() => import('../components/shell/ShellBottomNav'));

export const COLOR_THEME_OPTIONS = [
  { id: 'green', label: 'Green' },
  { id: 'blue', label: 'Blue' },
  { id: 'purple', label: 'Purple' },
  { id: 'neural-ink', label: 'Neural Ink' },
  { id: 'luminary-muse', label: 'Luminary Muse' },
  { id: 'wayfinder', label: 'Wayfinder Atlas' },
  { id: 'opus-canvas', label: 'Opus Canvas' },
  { id: 'ita-crimson-circuit', label: 'ITA Crimson Circuit' },
] as const;

export const LAYOUT_OPTIONS = [
  { id: 'classic', label: 'Classic' },
  { id: 'modern', label: 'Modern' },
  { id: 'minimal', label: 'Minimalist' },
  { id: 'wayfinder', label: 'Wayfinder Atlas' },
  { id: 'opus-canvas', label: 'Opus Canvas' },
] as const;

export type ColorThemeId = typeof COLOR_THEME_OPTIONS[number]['id'];
export type LayoutId = typeof LAYOUT_OPTIONS[number]['id'];
/** A saved layout: one of the five, or an admin-only test layout (ui/layouts/registry.ts). */
export type ShellLayoutId = LayoutId | TestLayoutId;

export const ORDER_VIEW_OPTIONS = [
  { id: 'classic', label: 'Classic workspace' },
] as const;

// Keep the legacy rail identifier readable so dormant preferences and the
// preserved implementation can be revived without a data migration.
export type OrderViewId = 'classic' | 'tabbed';
export const ORDER_CHAT_POSITION_OPTIONS = [
  { id: 'bottom', label: 'Bottom' },
  { id: 'right', label: 'Right side' },
  { id: 'left', label: 'Left side' },
] as const;
export type OrderChatPositionId = typeof ORDER_CHAT_POSITION_OPTIONS[number]['id'];

const ORDER_CHAT_POSITION_IDS = new Set<OrderChatPositionId>(ORDER_CHAT_POSITION_OPTIONS.map((option) => option.id));

export const LAYOUT_DESCRIPTIONS: Record<LayoutId, string> = {
  classic: 'Balanced dashboards',
  modern: 'Frosted glass surfaces',
  minimal: 'Compact + high focus',
  wayfinder: 'Immersive field journal',
  'opus-canvas': 'Thoughtful creative space',
};

const DEFAULT_COLOR_THEME: ColorThemeId = 'green';
const DEFAULT_LAYOUT: LayoutId = 'classic';

const COLOR_THEME_IDS = new Set<ColorThemeId>(COLOR_THEME_OPTIONS.map((option) => option.id));
const LAYOUT_IDS = new Set<LayoutId>(LAYOUT_OPTIONS.map((option) => option.id));

function isColorThemeId(value: string | null | undefined): value is ColorThemeId {
  if (!value) return false;
  return COLOR_THEME_IDS.has(value as ColorThemeId);
}

// Test layout ids are kept as saved for everyone; only admins get them applied (appliedLayout below).
function isLayoutId(value: string | null | undefined): value is ShellLayoutId {
  if (!value) return false;
  return LAYOUT_IDS.has(value as LayoutId) || isTestLayoutId(value);
}

const NO_TEST_LAYOUTS: readonly TestLayoutOption[] = [];
const COMMAND_SHORTCUT_LABEL = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || '') ? '⌘K' : 'Ctrl K';

function isOrderChatPositionId(value: string | null | undefined): value is OrderChatPositionId {
  if (!value) return false;
  return ORDER_CHAT_POSITION_IDS.has(value as OrderChatPositionId);
}

function getCachedShellTheme(authUserId: string | null | undefined): {
  colorTheme: ColorThemeId; layoutStyle: ShellLayoutId; holidayOptOut: string | null; appearanceMode: AppearanceMode;
} {
  const cached = readShellThemeCache(authUserId);
  const palette = paletteFor(cached?.colorTheme);
  return {
    colorTheme: isColorThemeId(palette) ? palette : DEFAULT_COLOR_THEME,
    layoutStyle: isLayoutId(cached?.layoutStyle) ? cached.layoutStyle : DEFAULT_LAYOUT,
    holidayOptOut: cached?.holidayOptOut ?? null,
    appearanceMode: appearanceModeFor(cached?.appearanceMode, cached?.colorTheme),
  };
}

/** The scheduled holiday showing in the shell, for the Edit profile picker (docs/holiday-themes.md). */
export type ShellHolidayTheme = { id: string; key: HolidayThemeKey; label: string; endsAt: string };

interface AppShellProps {
  supabase?: SupabaseClient;
  children: React.ReactNode;
}

const TOPBAR_MOBILE_BREAKPOINT = 1120;

interface ProfileMenuContextValue {
  accountName: string;
  session: Session | null;
  profile: SessionProfile | null;
  locationSummary: string;
  homeSiteLabel: string;
  activeLocationLabel: string;
  homeLocationOptions: Array<{ location_id: string; name: string }>;
  homeLocationOptionsLoading: boolean;
  regionOptions: AssignedRegion[];
  homeSiteDraft: string;
  setHomeSiteDraft: React.Dispatch<React.SetStateAction<string>>;
  homeSiteBusy: boolean;
  homeSiteStatus: string | null;
  homeSiteError: string | null;
  updateHomeSite: (nextValue: string) => Promise<void>;
  colorTheme: ColorThemeId;
  setColorTheme: (next: ColorThemeId) => void;
  appearanceMode: AppearanceMode;
  setAppearanceMode: (next: AppearanceMode) => void;
  appearanceTone: AppearanceTone;
  /** False once this person turned off the open holiday window; the next window shows again. */
  holidayThemeOn: boolean;
  setHolidayThemeOn: (on: boolean) => void;
  /** The open scheduled holiday window, whether or not this person opted out of it. */
  holidayTheme: ShellHolidayTheme | null;
  /** True while the holiday replaces this person's color theme. */
  holidayThemeShowing: boolean;
  /** The saved layout; a test layout shows only for admins (AppShell's appliedLayout). */
  layoutStyle: ShellLayoutId;
  setLayoutStyle: (next: ShellLayoutId) => void;
  orderViewPreference: OrderViewId;
  setOrderViewPreference: (next: OrderViewId) => void;
  orderChatPositionPreference: OrderChatPositionId;
  setOrderChatPositionPreference: (next: OrderChatPositionId) => void;
  colorThemeOptions: typeof COLOR_THEME_OPTIONS;
  layoutOptions: typeof LAYOUT_OPTIONS;
  layoutDescriptions: typeof LAYOUT_DESCRIPTIONS;
  /** Admins and super admins only; empty for everyone else. */
  testLayoutOptions: readonly TestLayoutOption[];
  orderViewOptions: typeof ORDER_VIEW_OPTIONS;
  orderChatPositionOptions: typeof ORDER_CHAT_POSITION_OPTIONS;
  userPrefsLoaded: boolean;
  logout: () => Promise<void>;
}

const ProfileMenuContext = React.createContext<ProfileMenuContextValue | null>(null);

export function useProfileMenuContext(): ProfileMenuContextValue {
  const ctx = React.useContext(ProfileMenuContext);
  if (!ctx) {
    throw new Error('useProfileMenuContext must be used within AppShell');
  }
  return ctx;
}

/**
 * A test layout's breadcrumb. It reads the page's title context itself, so only the crumb re-renders when a
 * page names itself. Below the section's own path the page is named: the record's code, or the route's title.
 */
function ShellBreadcrumb({ Breadcrumb, number, section, sectionTo, pathname }: {
  Breadcrumb: React.ComponentType<RouteBreadcrumbProps>;
  number: string;
  section: string;
  sectionTo: string;
  pathname: string;
}) {
  const context = usePageTitleContext();
  const depth = (path: string) => path.split('?')[0].split('/').filter(Boolean).length;
  const page = context && depth(pathname) > depth(sectionTo) ? context : null;
  return <Breadcrumb number={number} section={section} sectionTo={sectionTo} page={page} />;
}

export default function AppShell({ supabase, children }: AppShellProps) {
  const loc = useLocation();
  const eventsNavTarget = getEventsNavTarget(loc);
  const isClientPortalRoute = loc.pathname.startsWith('/client-portal');
  const isSignaturePortalRoute = loc.pathname.startsWith('/sign/documents/');
  // Signing in person hides the app chrome too. A customer handed the device at
  // a kiosk should be looking at a document, not at the staff member's
  // navigation, and a self-signer gets the same undistracted screen.
  const isInPersonSigningRoute = /^\/signature-documents\/[^/]+\/sign$/.test(loc.pathname);
  const topbarRef = React.useRef<HTMLElement | null>(null);
  const client = React.useMemo(() => supabase ?? getSupabaseClient(), [supabase]);
  const session = useSessionStore((s) => s.session);
  const authStatus = useSessionStore((s) => s.authStatus);
  const profileId = useSessionStore((s) => s.profileId);
  const sessionReady = useSessionStore((s) => s.sessionReady);
  const profile = useSessionStore((s) => s.profile);
  const securityLevel = useSessionStore((s) => s.securityLevel);
  // An ITA user opening a post-event questionnaire link keeps the app's menu (2026-10-22); customers, crew without
  // an account and client-portal guests see the survey alone.
  const isStaffPostEventLink = loc.pathname.startsWith('/q/')
    && authStatus === 'authenticated'
    && Boolean(securityLevel)
    && securityLevel !== 'guest';
  const isMinimalPortalRoute = isClientPortalRoute
    || isSignaturePortalRoute
    || (isPublicTokenPath(loc.pathname) && !isStaffPostEventLink)
    || isInPersonSigningRoute;
  const allowedLocationIds = useSessionStore((s) => s.allowedLocationIds);
  const assignedRegions = useSessionStore((s) => s.assignedRegions);
  const impersonatedRoles = useSessionStore((s) => s.impersonatedRoles);
  const setImpersonatedRoles = useSessionStore((s) => s.setImpersonatedRoles!);
  const loadContext = useSessionStore((s) => s.loadContext);
  const activeLocationId = useActiveLocationStore((state) => state.activeLocationId);
  const activeRegionId = useActiveLocationStore((state) => state.activeRegionId);
  const locationInitialised = useActiveLocationStore((state) => state.initialised);
  const initialiseActiveLocation = useActiveLocationStore((state) => state.initialise);
  const setActiveLocationId = useActiveLocationStore((state) => state.setActiveLocationId);
  const setActiveRegionId = useActiveLocationStore((state) => state.setActiveRegionId);
  const siteAccess = getSiteAccessState({
    sessionReady,
    securityLevel,
    allowedLocationIds,
  });
  const shellDataEnabled = (
    !isMinimalPortalRoute
    && authStatus === 'authenticated'
    && sessionReady
    && !siteAccess.isBlocked
  );
  const minimalShellActive = (
    isMinimalPortalRoute
    || authStatus !== 'authenticated'
    || !sessionReady
    || siteAccess.isBlocked
  );
  const { get: getPref, set: setPref, loaded: prefsLoaded } = useUserPrefs(
    client,
    shellDataEnabled,
  );
  const { isAdmin, isManager, isSuperAdmin, isImpersonating, canViewPage } = useRoles();
  // isSuperAdmin is false while previewing; the preview's stop control must still show then.
  const canPreviewAccess = isSuperAdmin || isImpersonating;
  const { access: installationAccess } = useInstallationAccess();
  const canSeeMaintenanceNav = canViewPage('maintenance');
  const canSeeInvoicingNav = canViewPage('invoicing');
  const canSeeSettingsNav = isAdmin && canViewPage('settings');
  const settingsNavTarget = '/admin/settings';
  const authUserId = session?.user?.id ?? null;
  usePersistImpersonation(client, shellDataEnabled);
  // Global auto-flush: flushes all pending autosaves on route navigation & beforeunload
  useGlobalAutoFlush();
  // A blocked internal user is still allowed to enter the app's assignment
  // screen. Record that access without mounting any site-scoped data sources.
  useAppPresence(shellDataEnabled);
  React.useEffect(() => {
    try {
      installPerfGuards();
    } catch {
      // noop
    }
  }, []);

  React.useEffect(() => {
    const enabled =
      process.env.NODE_ENV !== 'production' ||
      process.env.NEXT_PUBLIC_NET_TAP === '1' ||
      (typeof window !== 'undefined' && (window as any).__NET_TAP__ === 1);

    if (enabled) {
      installNetTap(
        [
          '/api/metrics/track',
          '/auth/v1/authorize',
          /login\.microsoftonline\.com\/.*authorize/i,
          '/auth/v1/callback',
        ],
        { logBodies: false },
      );
    }
  }, []);
  const { accountName, accountFirstName } = React.useMemo(() => {
    const first = (profile?.first_name || '').trim();
    const last = (profile?.last_name || '').trim();
    const full = profile?.full_name?.trim();
    if (full) {
      const spaceIdx = full.indexOf(' ');
      if (spaceIdx > 0) {
        return { accountName: full, accountFirstName: full.slice(0, spaceIdx), accountLastName: full.slice(spaceIdx + 1) };
      }
      return { accountName: full, accountFirstName: full, accountLastName: '' };
    }
    const parts = [first, last].filter((p) => p.length > 0);
    if (parts.length > 0) return { accountName: parts.join(' '), accountFirstName: first || last, accountLastName: first ? last : '' };
    const fallback = session?.user?.email || 'Account';
    return { accountName: fallback, accountFirstName: fallback, accountLastName: '' };
  }, [profile?.full_name, profile?.first_name, profile?.last_name, session?.user?.email]);
  const allowedCount = allowedLocationIds.length;
  const showScheduleNav = canViewPage('schedule');
  const scheduleNavActive = loc.pathname.startsWith('/schedule') || loc.pathname.startsWith('/calendar');
  const scheduleNavTarget = scheduleNavActive ? `${loc.pathname}${loc.search}${loc.hash}` : '/schedule';
  // One Inventory library (docs/inventory.md) for any of its four page grants.
  const showInventoryNav = canViewPage('inventory') || canViewPage('yearly_inventory')
    || canViewPage('admin_inventory') || canViewPage('alerts');

  const primaryNavItems = React.useMemo<PrimaryNavItem[]>(() => {
    const items: PrimaryNavItem[] = [];
    if (installationAccess.canSeeEvents && canViewPage('events')) {
      items.push({
        key: 'events', label: 'Events', to: eventsNavTarget,
        active: loc.pathname.startsWith('/events'), priority: 100,
      });
    }
    if (installationAccess.canAccess && canViewPage('installations')) {
      items.push({
        key: 'installations', label: 'Installations', to: '/installations',
        active: loc.pathname.startsWith('/installations'), priority: 95,
      });
    }
    if (canViewPage('service')) {
      items.push({
        key: 'service', label: 'Service', to: '/service',
        active: loc.pathname.startsWith('/service'), priority: 95,
      });
    }
    if (showScheduleNav) {
      items.push({
        key: 'schedule', label: 'Schedule', to: scheduleNavTarget,
        active: scheduleNavActive, priority: 94,
      });
    }
    if (canViewPage('clients')) {
      items.push({
        key: 'customers', label: 'Customers', to: '/clients',
        active: loc.pathname.startsWith('/clients'), priority: 90,
      });
    }
    if (canViewPage('venues')) {
      items.push({
        key: 'venues', label: 'Venues', to: '/venues',
        active: loc.pathname.startsWith('/venues'), priority: 85,
      });
    }
    if (canViewPage('transfers')) {
      items.push({
        key: 'warehouse', label: 'Warehouse', to: '/transfers',
        active: loc.pathname.startsWith('/transfers'), priority: 92,
      });
    }
    if (canViewPage('signatures')) {
      items.push({
        key: 'signatures', label: 'Signatures', to: '/signature-documents',
        active: loc.pathname.startsWith('/signature-documents'), priority: 78,
      });
    }
    if (showInventoryNav) {
      items.push({
        key: 'inventory', label: 'Inventory', to: '/inventory',
        active: loc.pathname.startsWith('/inventory') || loc.pathname.startsWith('/yearly-inventory'), priority: 82,
      });
    }
    if (canSeeInvoicingNav) {
      items.push({
        key: 'invoicing', label: 'Invoicing', to: '/invoicing',
        active: loc.pathname.startsWith('/invoicing'), priority: 80,
      });
    }
    if (canSeeMaintenanceNav) {
      items.push({
        key: 'maintenance', label: 'Maintenance', to: '/admin/maintenance',
        // The hub's pages and editors live at /admin/<page> (main.tsx); Settings and the super admin's todo don't.
        active: loc.pathname.startsWith('/admin/') && loc.pathname !== '/admin/settings' && loc.pathname !== '/admin/todo', priority: 45,
      });
    }
    if (canSeeSettingsNav) {
      items.push({
        key: 'settings', label: 'Settings', to: settingsNavTarget,
        active: loc.pathname === '/admin/settings', priority: 35,
      });
    }
    if (canViewPage('planner')) {
      items.push({
        key: 'planner', label: 'Planner', to: '/todo',
        active: loc.pathname === '/todo', priority: 75,
      });
    }
    return items;
  }, [
    installationAccess.canSeeEvents,
    installationAccess.canAccess,
    canViewPage,
    eventsNavTarget,
    loc.pathname,
    showScheduleNav,
    scheduleNavActive,
    scheduleNavTarget,
    showInventoryNav,
    canSeeInvoicingNav,
    canSeeMaintenanceNav,
    canSeeSettingsNav,
    settingsNavTarget,
  ]);

  const regionCount = assignedRegions.length;
  const locationSummary = React.useMemo(() => {
    if (allowedCount > 0) {
      return `${allowedCount} assigned`;
    }
    if (regionCount > 0) {
      return `${regionCount} region${regionCount === 1 ? '' : 's'}`;
    }
    return 'All locations';
  }, [allowedCount, regionCount]);
  const homeLocationId = profile?.home_location_id ?? null;
  const homeRegionId = profile?.home_region_id ?? null;
  const [homeSiteError, setHomeSiteError] = React.useState<string | null>(null);
  const locationsQuery = useQuery({
    queryKey: ['locations', profileId, ...(allowedLocationIds || [])],
    enabled: shellDataEnabled && !!authUserId,
    queryFn: () => runSupabaseQuery(
      () => fetchLocations(allowedLocationIds, { supabase: client }),
      'Unable to load locations.',
      'shell.locations',
    ),
  });
  const homeLocationOptions = locationsQuery.data ?? [];
  const homeLocationOptionsLoading = locationsQuery.isLoading;
  const locationOptionsError = locationsQuery.error
    ? safeErrorMessage(locationsQuery.error, 'Unable to load locations.')
    : null;
  const combinedHomeSiteError = homeSiteError || locationOptionsError;
  const initialHomeSiteValue = homeRegionId
    ? `region:${homeRegionId}`
    : homeLocationId
      ? `loc:${homeLocationId}`
      : '';
  const [homeSiteDraft, setHomeSiteDraft] = React.useState(initialHomeSiteValue);
  const [homeSiteBusy, setHomeSiteBusy] = React.useState(false);
  const [homeSiteStatus, setHomeSiteStatus] = React.useState<string | null>(null);
  const homeSiteStatusTimer = React.useRef<number | null>(null);
  const regionOptions = React.useMemo(() => {
    const map = new Map<string, AssignedRegion>();
    assignedRegions.forEach((region) => {
      map.set(region.region_id, region);
    });
    if (homeRegionId && !map.has(homeRegionId)) {
      map.set(homeRegionId, {
        region_id: homeRegionId,
        name: null,
        is_active: false,
        is_manager: false,
        grant_all_locations: false,
      });
    }
    return Array.from(map.values()).sort((a, b) => {
      const aName = (a.name || '').toLowerCase();
      const bName = (b.name || '').toLowerCase();
      if (aName === bName) return 0;
      return aName < bName ? -1 : 1;
    });
  }, [assignedRegions, homeRegionId]);
  const homeSiteLabel = React.useMemo(() => {
    if (homeRegionId) {
      const region = regionOptions.find((opt) => opt.region_id === homeRegionId);
      const baseName = region?.name || 'Region';
      const suffix = region && region.is_active === false ? ' (inactive)' : '';
      return `${baseName}${suffix}`;
    }
    if (homeLocationId) {
      const match = homeLocationOptions.find((opt) => opt.location_id === homeLocationId);
      return match?.name || 'Location';
    }
    return 'None';
  }, [homeRegionId, regionOptions, homeLocationId, homeLocationOptions]);

  const activeLocationLabel = React.useMemo(() => {
    if (activeRegionId) {
      const match = regionOptions.find((opt) => opt.region_id === activeRegionId);
      return match?.name || 'Region';
    }
    if (activeLocationId) {
      const match = homeLocationOptions.find((opt) => opt.location_id === activeLocationId);
      return match?.name || 'Location';
    }
    if (homeLocationOptionsLoading) return 'Loading locations…';
    if (homeLocationOptions.length === 0) return 'No accessible locations';
    return 'All accessible locations';
  }, [activeRegionId, activeLocationId, regionOptions, homeLocationOptions, homeLocationOptionsLoading]);

  // Compute the topbar select value: region:xxx, location uuid, or '' for all
  const topbarSelectValue = React.useMemo(() => {
    if (activeRegionId) return `region:${activeRegionId}`;
    return activeLocationId ?? '';
  }, [activeRegionId, activeLocationId]);

  const handleTopbarLocationChange = React.useCallback((value: string) => {
    if (value.startsWith('region:')) {
      setActiveRegionId(value.slice(7));
    } else {
      setActiveLocationId(value ? value : null);
    }
  }, [setActiveLocationId, setActiveRegionId]);

  const locationSelectId = React.useId();
  const navId = React.useId();
  const [isMobile, setIsMobile] = React.useState(() => (typeof window !== 'undefined' ? window.innerWidth <= TOPBAR_MOBILE_BREAKPOINT : false));
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [accountPanelOpen, setAccountPanelOpen] = React.useState(false);
  const desktopAccountMenuRef = React.useRef<HTMLDivElement | null>(null);
  const mobileAccountPanelRef = React.useRef<HTMLDivElement | null>(null);
  const mobileNavPanelRef = React.useRef<HTMLDivElement | null>(null);
  const [collapsed, setCollapsed] = React.useState(false);
  const [itemsDebugEnabled, setItemsDebugEnabled] = React.useState(() => {
    if (typeof window === 'undefined') return false;
    try { return sessionStorage.getItem('itemsPanelDebug') === '1'; } catch { return false; }
  });
  const itemsDebugPopoutRef = React.useRef<Window | null>(null);
  const itemsDebugPopoutRootRef = React.useRef<any>(null);
  const isMountedRef = React.useRef(true);
  // Use a ref to store the setter so callbacks don't capture stale closures
  const setItemsDebugEnabledRef = React.useRef(setItemsDebugEnabled);
  
  // Track mount status and keep setter ref updated
  React.useEffect(() => {
    isMountedRef.current = true;
    setItemsDebugEnabledRef.current = setItemsDebugEnabled;
    return () => {
      isMountedRef.current = false;
    };
  }, [setItemsDebugEnabled]);
  
  const closeItemsDebugPopout = React.useCallback(() => {
    const root = itemsDebugPopoutRootRef.current;
    if (root && typeof root.unmount === 'function') {
      try { root.unmount(); } catch { /* ignore */ }
    }
    itemsDebugPopoutRootRef.current = null;
    const win = itemsDebugPopoutRef.current;
    if (win && !win.closed) {
      // Remove the beforeunload listener before closing
      try {
        const handler = (win as any).__itemsDebugBeforeUnload;
        if (handler) {
          win.removeEventListener('beforeunload', handler);
          delete (win as any).__itemsDebugBeforeUnload;
        }
      } catch { /* ignore */ }
      try { win.close(); } catch { /* ignore */ }
    }
    itemsDebugPopoutRef.current = null;
  }, []);
  const openItemsDebugPopout = React.useCallback(async () => {
    if (typeof window === 'undefined') return null;
    const existing = itemsDebugPopoutRef.current;
    if (existing && !existing.closed) {
      try { existing.focus(); } catch { /* ignore */ }
      return existing;
    }
    if (!isMountedRef.current) return null;
    let w: Window | null = null;
    try {
      // Check if we're in a valid execution context before calling window.open
      if (typeof window === 'undefined' || typeof window.open !== 'function') {
        return null;
      }
      // Use a synchronous check to ensure we're in a valid context
      // The error "callback is no longer runnable" happens when the execution context has changed
      // We'll catch and ignore this specific error
      try {
        w = window.open('', '_blank', 'width=900,height=600');
      } catch (openError: any) {
        // Check if it's the specific "callback is no longer runnable" error
        if (openError?.message?.includes('callback is no longer runnable') || 
            openError?.message?.includes('The provided callback')) {
          // Silently ignore this specific error - it happens when the execution context changes
          return null;
        }
        // Re-throw other errors
        throw openError;
      }
    } catch (e) {
      // Silently fail if window.open is blocked or invalid
      return null;
    }
    if (!w || w.closed) return null;
    
    // Verify the window is still accessible before proceeding
    try {
      if (w.document) {
        itemsDebugPopoutRef.current = w;
        w.document.title = 'Items Debug Console';
      } else {
        return null;
      }
    } catch (e) {
      // Window is no longer accessible
      return null;
    }
    
    // Listen for window close to turn off debug - only if still mounted
    // Use the ref to avoid stale closure issues
    const handleBeforeUnload = () => {
      // Check if component is still mounted before calling setState
      // Use the ref to avoid stale closure issues
      if (isMountedRef.current && setItemsDebugEnabledRef.current) {
        try {
          setItemsDebugEnabledRef.current(false);
        } catch (e) {
          // Ignore errors if component is unmounting or callback is invalid
        }
      }
    };
    // Store handler reference for cleanup
    (w as any).__itemsDebugBeforeUnload = handleBeforeUnload;
    try {
      w.addEventListener('beforeunload', handleBeforeUnload);
    } catch (e) {
      // Ignore if window is already closed or callback can't be attached
    }
    
    let mount: HTMLElement | null = null;
    try {
      mount = w.document.createElement('div');
      w.document.body.style.margin = '0';
      w.document.body.appendChild(mount);
    } catch (e) {
      // Window might be closed or inaccessible
      return null;
    }
    
    try {
      const [{ default: DebugConsole }, rdom] = await Promise.all([
        import('../components/DebugConsole'),
        import('react-dom/client'),
      ]);
      if (!isMountedRef.current) {
        return null;
      }
      // Re-check window validity after async operation
      if (!itemsDebugPopoutRef.current || itemsDebugPopoutRef.current.closed || !mount) {
        return null;
      }
      const element = React.createElement(DebugConsole, { height: '100vh', channels: ['items'], allowPopout: false, title: 'Items Debug' });
      const root = (rdom as any).createRoot ? (rdom as any).createRoot(mount) : null;
      if (root) {
        root.render(element);
        itemsDebugPopoutRootRef.current = root;
      } else if ((rdom as any).render) {
        (rdom as any).render(element, mount);
      }
    } catch (err) {
      /* ignore popout render errors */
    }
    return w;
  }, []);

  const initialShellTheme = React.useMemo(() => getCachedShellTheme(authUserId), [authUserId]);
  const [colorTheme, setColorTheme] = React.useState<ColorThemeId>(() => initialShellTheme.colorTheme);
  const [layoutStyle, setLayoutStyle] = React.useState<ShellLayoutId>(() => initialShellTheme.layoutStyle);
  const [holidayOptOut, setHolidayOptOut] = React.useState<string | null>(() => initialShellTheme.holidayOptOut);
  const [appearanceMode, setAppearanceMode] = React.useState<AppearanceMode>(() => initialShellTheme.appearanceMode);
  const deviceDark = useDeviceDarkMode();
  const appearanceTone = appearanceToneFor(appearanceMode, deviceDark);
  const [orderViewPreference, setOrderViewPreference] = React.useState<OrderViewId>('classic');
  const [orderChatPositionPreference, setOrderChatPositionPreference] = React.useState<OrderChatPositionId>('bottom');
  const { scheduled: scheduledHoliday, preview: holidayPreview } = useScheduledHolidayTheme();
  const colorThemeRef = React.useRef(colorTheme);
  const layoutStyleRef = React.useRef(layoutStyle);
  const holidayOptOutRef = React.useRef(holidayOptOut);
  const appearanceModeRef = React.useRef(appearanceMode);
  const scheduledHolidayRef = React.useRef(scheduledHoliday);
  colorThemeRef.current = colorTheme;
  layoutStyleRef.current = layoutStyle;
  holidayOptOutRef.current = holidayOptOut;
  appearanceModeRef.current = appearanceMode;
  scheduledHolidayRef.current = scheduledHoliday;
  React.useEffect(() => {
    const next = homeRegionId
      ? `region:${homeRegionId}`
      : homeLocationId
        ? `loc:${homeLocationId}`
        : '';
    setHomeSiteDraft(next);
  }, [homeLocationId, homeRegionId]);
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const key = 'itemsPanelDebug';
    try {
      if (itemsDebugEnabled) sessionStorage.setItem(key, '1');
      else sessionStorage.removeItem(key);
    } catch { /* ignore */ }
    try {
      window.dispatchEvent(new CustomEvent('items-panel-debug-toggled', { detail: { enabled: itemsDebugEnabled } }));
    } catch { /* ignore */ }
    if (itemsDebugEnabled && isMountedRef.current) {
      // Use setTimeout to ensure we're in a valid execution context
      // This prevents "callback is no longer runnable" errors
      const timeoutId = setTimeout(() => {
        if (isMountedRef.current) {
          openItemsDebugPopout().catch((err) => {
            // Silently ignore errors from window.open
            console.debug('Items debug popout error (ignored):', err);
          });
        }
      }, 0);
      return () => clearTimeout(timeoutId);
    } else {
      closeItemsDebugPopout();
    }
  }, [closeItemsDebugPopout, itemsDebugEnabled, openItemsDebugPopout]);
  React.useEffect(() => () => { closeItemsDebugPopout(); }, [closeItemsDebugPopout]);

  React.useEffect(() => {
    if (!prefsLoaded) return;
    const storedPosition = getPref<OrderChatPositionId | null>('order.chat.position.default', null);
    if (isOrderChatPositionId(storedPosition)) {
      setOrderChatPositionPreference(storedPosition);
      return;
    }
    setOrderChatPositionPreference('bottom');
  }, [getPref, prefsLoaded]);

  // Sync theme and layout from useUserPrefs (cross-device) if available. Only from the server read: until
  // the session is ready the hook holds a local copy (`ui_prefs:anon` before the profile is known), and a
  // stale one turned a Dark user's loading screen light on every load (docs/ui-preferences.md).
  React.useEffect(() => {
    if (!prefsLoaded || !shellDataEnabled) return;
    const savedColor = getPref<string | null>('app.colorTheme', null);
    const storedColor = paletteFor(savedColor);
    const storedLayout = getPref<ShellLayoutId | null>('app.layoutStyle', null);
    const storedOptOut = getPref<unknown>('app.holidayOptOut', null);
    const storedMode = getPref<unknown>('app.appearanceMode', null);
    const nextOptOut = typeof storedOptOut === 'string' && storedOptOut ? storedOptOut : null;
    const cached = readShellThemeCache(authUserId);
    const cachedColor = paletteFor(cached?.colorTheme);
    const nextColor = isColorThemeId(storedColor)
      ? storedColor
      : isColorThemeId(cachedColor)
        ? cachedColor
        : DEFAULT_COLOR_THEME;
    const nextLayout = isLayoutId(storedLayout)
      ? storedLayout
      : isLayoutId(cached?.layoutStyle)
        ? cached.layoutStyle
        : DEFAULT_LAYOUT;
    // Only the saved preferences decide the mode, so every device agrees: none saved follows System.
    const nextMode = appearanceModeFor(storedMode, savedColor);

    if (colorThemeRef.current !== nextColor) {
      setColorTheme(nextColor);
    }
    if (layoutStyleRef.current !== nextLayout) {
      setLayoutStyle(nextLayout);
    }
    if (holidayOptOutRef.current !== nextOptOut) {
      setHolidayOptOut(nextOptOut);
    }
    if (appearanceModeRef.current !== nextMode) setAppearanceMode(nextMode);
    writeShellThemeCache({
      authUserId: authUserId ?? null,
      profileId: profileId ?? null,
      colorTheme: nextColor,
      layoutStyle: nextLayout,
      holidayOptOut: nextOptOut,
      appearanceMode: nextMode,
    });
  }, [authUserId, getPref, prefsLoaded, profileId, shellDataEnabled]);

  const handleOrderViewPreference = React.useCallback(
    (next: OrderViewId) => {
      setOrderViewPreference(next);
      setPref('order.layout.view', next);
      setPref('order.layout.classicPreferred', next === 'classic');
    },
    [setPref],
  );

  const handleOrderChatPositionPreference = React.useCallback(
    (next: OrderChatPositionId) => {
      setOrderChatPositionPreference(next);
      setPref('order.chat.position.default', next);
    },
    [setPref],
  );

  const locationOptionIds = React.useMemo(() => new Set(homeLocationOptions.map((opt) => opt.location_id)), [homeLocationOptions]);
  const locationListFetched = locationsQuery.isFetched && !homeLocationOptionsLoading;

  // Every page load starts at the person's home (docs/ui-preferences.md, "Active site"): a home
  // region that is assigned, active and grants all its sites, else a home site they can reach, else
  // no site, and the guard below picks for people who must have one. It waits for the signed-in
  // shell, which brings the profile and its assignments, but not for the site list: the pages that
  // wait for `initialised` would wait too, and the check below drops a home missing from the list.
  // A site picked during a visit is not saved, so the next load starts at the home again.
  React.useEffect(() => {
    if (locationInitialised || !shellDataEnabled) return;
    const homeRegion = homeRegionId
      ? regionOptions.find((r) => r.region_id === homeRegionId && r.is_active && r.grant_all_locations)
      : undefined;
    if (homeRegion) {
      initialiseActiveLocation(null);
      setActiveRegionId(homeRegion.region_id);
      return;
    }
    const homeReachable = homeLocationId !== null
      && (allowedLocationIds.length === 0 || allowedLocationIds.includes(homeLocationId));
    initialiseActiveLocation(homeReachable ? homeLocationId : null);
  }, [
    locationInitialised,
    shellDataEnabled,
    homeRegionId,
    homeLocationId,
    regionOptions,
    allowedLocationIds,
    initialiseActiveLocation,
    setActiveRegionId,
  ]);

  React.useEffect(() => {
    // Until the site list arrives the options are empty; clearing then would drop the home site.
    if (!locationInitialised || !locationListFetched) return;
    const validIds = locationOptionIds;
    if (validIds.size === 0) {
      if (activeLocationId !== null) {
        setActiveLocationId(null);
      }
      return;
    }
    if (activeLocationId && !validIds.has(activeLocationId)) {
      const homeLoc = profile?.home_location_id ?? null;
      if (homeLoc && validIds.has(homeLoc)) {
        setActiveLocationId(homeLoc);
        return;
      }
      const fallback = homeLocationOptions[0]?.location_id ?? null;
      if (fallback !== activeLocationId) {
        setActiveLocationId(fallback ?? null);
      }
    }
  }, [
    locationInitialised,
    locationListFetched,
    locationOptionIds,
    activeLocationId,
    setActiveLocationId,
    profile?.home_location_id,
    homeLocationOptions,
  ]);

  // Guard: if user role cannot see "All accessible locations", ensure a region or location is selected
  React.useEffect(() => {
    if (!locationInitialised) return;
    if (isAdmin || isManager) return;
    if (activeLocationId || activeRegionId) return;
    if (homeLocationOptionsLoading) return;
    const grantRegion = regionOptions.find((r) => r.is_active && r.grant_all_locations && r.name);
    if (grantRegion) {
      setActiveRegionId(grantRegion.region_id);
      return;
    }
    const fallback = homeLocationOptions[0]?.location_id ?? null;
    if (fallback) {
      setActiveLocationId(fallback);
    }
  }, [locationInitialised, isAdmin, isManager, activeLocationId, activeRegionId, homeLocationOptionsLoading, regionOptions, homeLocationOptions, setActiveRegionId, setActiveLocationId]);

  React.useEffect(() => {
    return () => {
      if (homeSiteStatusTimer.current) {
        window.clearTimeout(homeSiteStatusTimer.current);
      }
    };
  }, []);

  const updateHomeSite = React.useCallback(async (nextValue: string) => {
    if (!profileId) return;
    setHomeSiteBusy(true);
    setHomeSiteStatus(null);
    setHomeSiteError(null);
    try {
      let nextHomeLocationId: string | null = null;
      let nextHomeRegionId: string | null = null;
      if (!nextValue) {
        nextHomeLocationId = null;
        nextHomeRegionId = null;
      } else if (nextValue.startsWith('region:')) {
        const regionId = nextValue.slice(7);
        nextHomeRegionId = regionId ? regionId : null;
      } else if (nextValue.startsWith('loc:')) {
        const locationId = nextValue.slice(4);
        nextHomeLocationId = locationId ? locationId : null;
      } else {
        nextHomeLocationId = nextValue;
      }
      const error = await updateProfileById(
        profileId,
        {
          home_location_id: nextHomeLocationId,
          home_region_id: nextHomeRegionId,
        },
        client,
      );
      if (error) throw error;
      await refreshSessionContext(loadContext, client);
      setHomeSiteStatus('Saved');
      if (homeSiteStatusTimer.current) {
        window.clearTimeout(homeSiteStatusTimer.current);
      }
      homeSiteStatusTimer.current = window.setTimeout(() => setHomeSiteStatus(null), 2000) as unknown as number;
    } catch (err: unknown) {
      setHomeSiteError(safeErrorMessage(err, 'Unable to update home site.'));
      const resetValue = homeRegionId
        ? `region:${homeRegionId}`
        : homeLocationId
          ? `loc:${homeLocationId}`
          : '';
      setHomeSiteDraft(resetValue);
    } finally {
      setHomeSiteBusy(false);
    }
  }, [client, profileId, loadContext, homeRegionId, homeLocationId]);

  // A scheduled holiday replaces the color theme on screen only; the saved preference never changes.
  const holidayThemeOn = !(scheduledHoliday && holidayOptOut === scheduledHoliday.id);
  const shellTheme = React.useMemo(() => resolveShellTheme({
    colorTheme,
    scheduled: scheduledHoliday,
    optOutId: holidayOptOut,
    preview: holidayPreview,
    appearanceTone,
  }), [appearanceTone, colorTheme, holidayOptOut, holidayPreview, scheduledHoliday]);

  // A saved test layout shows only for admins and super admins (not while previewing another user's
  // access), and only once its stylesheet has arrived; until then the shell keeps what it showed, and
  // Classic for everyone else. The saved choice itself is never rewritten (docs/test-layouts.md).
  // The minimal shell (the client portal, signing and public links) shows what customers see, so it never
  // takes one: its pages carry the public layout (PUBLIC_SHELL_LAYOUT below) and have no rail or nav.
  const savedTestLayout = isTestLayoutId(layoutStyle) ? getTestLayout(layoutStyle) : null;
  const testLayoutAllowed = !!savedTestLayout && isAdmin && !minimalShellActive;
  // Each loaded test layout's page kit; the layout applies only once its stylesheet and its kit have both arrived.
  const [loadedTestLayouts, setLoadedTestLayouts] = React.useState<ReadonlyMap<TestLayoutId, PageKit>>(() => new Map());
  React.useEffect(() => {
    if (!savedTestLayout || !testLayoutAllowed || loadedTestLayouts.has(savedTestLayout.id)) return undefined;
    let cancelled = false;
    Promise.all([savedTestLayout.loadStyles(), savedTestLayout.loadPageKit()])
      .then(([, kit]) => {
        if (!cancelled) setLoadedTestLayouts((loaded) => new Map(loaded).set(savedTestLayout.id, kit));
      })
      .catch((error) => {
        console.warn('[AppShell] test layout styles or page kit failed to load; staying on the current layout', error);
      });
    return () => { cancelled = true; };
  }, [loadedTestLayouts, savedTestLayout, testLayoutAllowed]);
  const lastAppliedLayoutRef = React.useRef<ShellLayoutId>(DEFAULT_LAYOUT);
  let appliedLayout: ShellLayoutId = layoutStyle;
  if (savedTestLayout) {
    if (!testLayoutAllowed) appliedLayout = DEFAULT_LAYOUT;
    else if (!loadedTestLayouts.has(savedTestLayout.id)) {
      appliedLayout = isTestLayoutId(lastAppliedLayoutRef.current) ? DEFAULT_LAYOUT : lastAppliedLayoutRef.current;
    }
  }
  lastAppliedLayoutRef.current = appliedLayout;
  const appliedTestLayout = isTestLayoutId(appliedLayout) ? getTestLayout(appliedLayout) : null;
  const shellCaps = appliedTestLayout?.capabilities ?? null;
  const appliedPageKit = appliedTestLayout ? loadedTestLayouts.get(appliedTestLayout.id) ?? null : null;
  // Pages read the applied layout's page features and kit here; memoised on the layout alone (docs/test-layouts.md).
  const shellLayoutValue = React.useMemo<ShellLayoutValue>(() => ({
    layout: appliedLayout,
    features: appliedTestLayout?.pageFeatures ?? NO_PAGE_FEATURES,
    kit: appliedPageKit,
  }), [appliedLayout, appliedPageKit, appliedTestLayout]);
  // Field's breadcrumb and phone title (docs/test-layouts.md, "Page features"): the active route's place in
  // the nav, numbered as the rail numbers it.
  const LayoutBreadcrumb = shellLayoutValue.features.breadcrumb ? appliedPageKit?.RouteBreadcrumb : undefined;
  const LayoutRouteTitle = shellLayoutValue.features.breadcrumb ? appliedPageKit?.RouteTitle : undefined;
  const activeRouteIndex = LayoutBreadcrumb || LayoutRouteTitle ? primaryNavItems.findIndex((item) => item.active) : -1;
  const activeRoute = activeRouteIndex >= 0 ? primaryNavItems[activeRouteIndex] : null;
  const activeRouteNumber = String(activeRouteIndex + 1).padStart(2, '0');
  const railMinWidth = shellCaps?.sideRail ? shellCaps.railMinWidth : 100000;
  const railWidthReached = useMediaQuery(`(min-width: ${railMinWidth}px)`);
  const railActive = !!shellCaps?.sideRail && railWidthReached;
  // A layout can keep its nav in the bar below the mobile breakpoint without a rail (Studio's tablet masthead).
  const navWidthReached = useMediaQuery(`(min-width: ${shellCaps?.navMinWidth ?? 100000}px)`);
  const navInBar = !isMobile || railActive || (!!shellCaps?.navMinWidth && navWidthReached);

  React.useLayoutEffect(() => {
    applyShellThemeAttributes({
      colorTheme: shellTheme.dataTheme,
      layoutStyle: appliedLayout,
      colorScheme: colorTheme,
      appearanceMode,
      appearanceTone,
      holiday: shellTheme.holiday && shellTheme.tone ? { key: shellTheme.holiday, tone: shellTheme.tone } : null,
    });
  }, [appearanceMode, appearanceTone, appliedLayout, colorTheme, shellTheme]);

  // Save only explicit choices. Hydration may change the displayed state while
  // this render still holds the previous theme; an effect would save that stale
  // value back and start a feedback loop with the next preference read.
  // Palette and mode choices leave the holiday opt-out alone.
  const handleColorThemePreference = React.useCallback((next: ColorThemeId) => {
    colorThemeRef.current = next;
    setColorTheme(next);
    setPref('app.colorTheme', next);
    // Freeze the displayed legacy/default mode on this explicit choice, including System.
    setPref('app.appearanceMode', appearanceModeRef.current);
    writeShellThemeCache({
      authUserId: authUserId ?? null,
      profileId: profileId ?? null,
      colorTheme: next,
      layoutStyle: layoutStyleRef.current,
      holidayOptOut: holidayOptOutRef.current,
      appearanceMode: appearanceModeRef.current,
    });
  }, [authUserId, profileId, setPref]);

  const handleAppearanceModePreference = React.useCallback((next: AppearanceMode) => {
    appearanceModeRef.current = next;
    setAppearanceMode(next);
    setPref('app.appearanceMode', next);
    writeShellThemeCache({
      authUserId: authUserId ?? null,
      profileId: profileId ?? null,
      colorTheme: colorThemeRef.current,
      layoutStyle: layoutStyleRef.current,
      appearanceMode: next,
      holidayOptOut: holidayOptOutRef.current,
    });
  }, [authUserId, profileId, setPref]);

  // Turning the holiday off opts out of the open window only; the next window shows again.
  const handleHolidayThemeOn = React.useCallback((on: boolean) => {
    const open = scheduledHolidayRef.current;
    if (!open || !isAvailableHolidayTheme(open.themeKey)) return;
    const next = on ? null : open.id;
    holidayOptOutRef.current = next;
    setHolidayOptOut(next);
    setPref('app.holidayOptOut', next ?? undefined);
    writeShellThemeCache({
      authUserId: authUserId ?? null,
      profileId: profileId ?? null,
      colorTheme: colorThemeRef.current,
      layoutStyle: layoutStyleRef.current,
      holidayOptOut: next,
      appearanceMode: appearanceModeRef.current,
    });
  }, [authUserId, profileId, setPref]);

  const handleLayoutStylePreference = React.useCallback((next: ShellLayoutId) => {
    // The picker lists test layouts for admins only; refuse one from anywhere else.
    if (isTestLayoutId(next) && !isAdmin) return;
    layoutStyleRef.current = next;
    setLayoutStyle(next);
    setPref('app.layoutStyle', next);
    writeShellThemeCache({
      authUserId: authUserId ?? null,
      profileId: profileId ?? null,
      colorTheme: colorThemeRef.current,
      layoutStyle: next,
      appearanceMode: appearanceModeRef.current,
    });
  }, [authUserId, isAdmin, profileId, setPref]);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleResize = () => {
      const mobile = window.innerWidth <= TOPBAR_MOBILE_BREAKPOINT;
      setIsMobile(mobile);
      if (!mobile) {
        setCollapsed(false);
        setMenuOpen(false);
      } else {
        setCollapsed(window.scrollY > 10);
      }
    };

    const handleScroll = () => {
      if (window.innerWidth <= TOPBAR_MOBILE_BREAKPOINT) {
        setCollapsed(window.scrollY > 10);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  React.useEffect(() => {
    if (!isMobile) {
      setMenuOpen(false);
      setAccountPanelOpen(false);
    }
  }, [isMobile]);

  React.useEffect(() => {
    setAccountPanelOpen(false);
  }, [loc.pathname]);

  React.useEffect(() => {
    if (typeof document === 'undefined') return;
    if (!isMobile) {
      document.body.removeAttribute('data-nav-open');
      document.body.style.removeProperty('overflow');
      return;
    }
    const anyPanelOpen = menuOpen || accountPanelOpen;
    if (!anyPanelOpen) {
      document.body.setAttribute('data-nav-open', 'false');
      document.body.style.removeProperty('overflow');
      return;
    }
    const body = document.body;
    const previousOverflow = body.style.overflow;
    body.setAttribute('data-nav-open', 'true');
    body.style.overflow = 'hidden';
    return () => {
      body.setAttribute('data-nav-open', 'false');
      body.style.overflow = previousOverflow;
    };
  }, [isMobile, menuOpen, accountPanelOpen]);

  React.useEffect(() => {
    if (!isMobile) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (accountPanelOpen) setAccountPanelOpen(false);
        else setMenuOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isMobile, menuOpen, accountPanelOpen]);

  React.useEffect(() => {
    if (!isMobile || !accountPanelOpen) return;
    function handlePointerOutside(event: MouseEvent | TouchEvent) {
      const target = event.target as Node | null;
      const panel = mobileAccountPanelRef.current;
      if (panel && target && !panel.contains(target)) {
        setAccountPanelOpen(false);
      }
    }
    document.addEventListener('mousedown', handlePointerOutside, { capture: true });
    document.addEventListener('touchstart', handlePointerOutside, { capture: true });
    return () => {
      document.removeEventListener('mousedown', handlePointerOutside, { capture: true });
      document.removeEventListener('touchstart', handlePointerOutside, { capture: true });
    };
  }, [isMobile, accountPanelOpen]);

  React.useEffect(() => {
    if (isMobile || !accountPanelOpen) return;
    function handlePointerOutside(event: MouseEvent | TouchEvent) {
      const target = event.target as Node | null;
      if (target && !desktopAccountMenuRef.current?.contains(target)) {
        setAccountPanelOpen(false);
      }
    }
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setAccountPanelOpen(false);
    }
    document.addEventListener('mousedown', handlePointerOutside, { capture: true });
    document.addEventListener('touchstart', handlePointerOutside, { capture: true });
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerOutside, { capture: true });
      document.removeEventListener('touchstart', handlePointerOutside, { capture: true });
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isMobile, accountPanelOpen]);

  React.useEffect(() => {
    if (!isMobile || !menuOpen) return;
    function handlePointerOutside(event: MouseEvent | TouchEvent) {
      const target = event.target as Node | null;
      const panel = mobileNavPanelRef.current;
      // The bar stays above the menu's scrim, so ☰ is outside the panel: let its own click toggle the
      // menu closed rather than closing here and reopening on that click.
      if (target instanceof Element && target.closest('.topbar-actions__menu-button')) return;
      if (panel && target && !panel.contains(target)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handlePointerOutside, { capture: true });
    document.addEventListener('touchstart', handlePointerOutside, { capture: true });
    return () => {
      document.removeEventListener('mousedown', handlePointerOutside, { capture: true });
      document.removeEventListener('touchstart', handlePointerOutside, { capture: true });
    };
  }, [isMobile, menuOpen]);

  React.useEffect(() => {
    setMenuOpen(false);
  }, [loc.pathname]);

  const logout = React.useCallback(async () => {
    const activeSession = useSessionStore.getState().session;
    if (activeSession?.user?.id) {
      try { await client.rpc('clear_impersonation'); } catch { /* noop */ }
    }
    setImpersonatedRoles(null);
    await requestPrincipalLogout({
      reason: 'manual_logout',
      preservePublicRoute: isClientPortalRoute,
    });
  }, [client, isClientPortalRoute, setImpersonatedRoles]);

  // Test layout shell pieces (docs/test-layouts.md). The public layouts get none of these.
  const navigate = useNavigate();
  const [paletteOpen, setPaletteOpen] = React.useState(false);
  // Classic has the palette too, opened from its own search button (docs/ui-preferences.md); the other
  // public layouts have none.
  const classicSearch = appliedLayout === 'classic';
  const paletteEnabled = !!shellCaps?.commandPalette || classicSearch;
  const railNavItems = React.useMemo<PrimaryNavItem[]>(() => (
    shellCaps?.navIcons ? primaryNavItems.map((item) => ({ ...item, icon: navIcon(item.key) })) : primaryNavItems
  ), [primaryNavItems, shellCaps?.navIcons]);
  React.useEffect(() => {
    if (!paletteEnabled) {
      setPaletteOpen(false);
      return undefined;
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.isComposing || event.keyCode === 229 || typeof event.key !== 'string') return;
      if (event.key.toLowerCase() !== 'k' || !(event.ctrlKey || event.metaKey) || event.altKey || event.shiftKey) return;
      // Rich text editors keep Ctrl+K for links.
      const target = event.target;
      if (target instanceof HTMLElement && target.isContentEditable) return;
      event.preventDefault();
      setPaletteOpen((open) => !open);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [paletteEnabled]);
  React.useEffect(() => { setPaletteOpen(false); }, [loc.pathname]);
  const paletteItems = React.useMemo<CommandPaletteItem[]>(() => {
    if (!paletteEnabled) return [];
    const go = (to: string) => () => navigate(to);
    return [
      { key: 'home', label: 'Home', group: 'Go to', icon: navIcon('home'), keywords: 'dashboard', run: go('/') },
      ...primaryNavItems.map((item) => ({ key: item.key, label: item.label, group: 'Go to', icon: navIcon(item.key), run: go(item.to) })),
      { key: 'edit-profile', label: 'Edit profile', group: 'Actions', keywords: 'account', run: go('/profile/edit') },
      { key: 'appearance', label: 'Change layout or colors', group: 'Actions', keywords: 'appearance theme dark light workspace', run: go('/profile/edit?tab=preferences') },
      { key: 'sign-out', label: 'Sign out', group: 'Actions', keywords: 'log out logout', run: () => { void logout(); } },
    ];
  }, [logout, navigate, paletteEnabled, primaryNavItems]);
  // Record search in the palette (docs/record-search.md): only for the pages this person may open.
  const canSearchEvents = canViewPage('events');
  const canSearchCustomers = canViewPage('clients');
  const canSearchVenues = canViewPage('venues');
  const paletteSearch = React.useMemo<CommandPaletteSearch | undefined>(() => {
    if (!paletteEnabled || !(canSearchEvents || canSearchCustomers || canSearchVenues)) return undefined;
    const go = (to: string) => () => navigate(to);
    const place = (city: string | null, state: string | null) => [city, state].filter(Boolean).join(', ') || undefined;
    return async (query, signal) => {
      let found;
      try {
        found = await searchRecords(query, signal);
      } catch (error) {
        // Someone the server doesn't count as internal staff gets no records rather than an error.
        if ((error as { code?: string } | null)?.code === '42501') return [];
        throw error;
      }
      return [
        ...(canSearchEvents ? found.events.map((event) => ({
          key: `event:${event.id}`, label: event.name || event.code || 'Event', group: 'Events', icon: navIcon('events'),
          detail: [event.code, event.status ? event.status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : null].filter(Boolean).join(' · ') || undefined,
          run: go(`/events/${event.id}`),
        })) : []),
        ...(canSearchCustomers ? found.customers.map((customer) => ({
          key: `customer:${customer.id}`, label: customer.name, group: 'Customers', icon: navIcon('customers'),
          detail: place(customer.city, customer.state), run: go(`/clients/${customer.id}`),
        })) : []),
        ...(canSearchVenues ? found.venues.map((venue) => ({
          key: `venue:${venue.id}`, label: venue.name, group: 'Venues', icon: navIcon('venues'),
          detail: place(venue.city, venue.state), run: go(`/venues/${venue.id}`),
        })) : []),
      ];
    };
  }, [canSearchCustomers, canSearchEvents, canSearchVenues, navigate, paletteEnabled]);
  const bottomNavItems = React.useMemo<ShellBottomNavItem[]>(() => {
    const wanted = shellCaps?.bottomNav;
    if (!wanted) return [];
    const byKey = new Map(primaryNavItems.map((item, index) => [item.key, { item, route: String(index + 1).padStart(2, '0') }]));
    const items: ShellBottomNavItem[] = [];
    wanted.forEach((key) => {
      if (key === 'home') {
        items.push({ key, label: 'Home', icon: navIcon('home'), to: '/', active: loc.pathname === '/' });
      } else if (key === 'search') {
        if (shellCaps?.commandPalette) items.push({ key, label: 'Search', icon: navIcon('search'), onSelect: () => setPaletteOpen(true), hasPopup: 'dialog', expanded: paletteOpen });
      } else if (key === 'menu' || key === 'more' || key === 'routes') {
        const label = key === 'more' ? 'More' : key === 'routes' ? 'Routes' : 'Menu';
        items.push({ key, label, icon: navIcon(key === 'routes' ? 'menu' : key), onSelect: () => setMenuOpen((open) => !open), hasPopup: 'dialog', expanded: menuOpen, controls: navId });
      } else {
        // The route number is the page's place in the nav, as Field's rail counts it.
        const entry = byKey.get(key);
        if (entry) items.push({ key, label: entry.item.label, icon: navIcon(key), to: entry.item.to, active: entry.item.active, route: entry.route });
      }
    });
    return items;
  }, [loc.pathname, menuOpen, navId, paletteOpen, primaryNavItems, shellCaps]);

  const [topbarPlaceholderHeight, setTopbarPlaceholderHeight] = React.useState(0);

  // Dynamically set --app-header-height and topbar placeholder height (for fixed topbar spacer)
  React.useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    const root = document.documentElement;
    let raf = 0;

    const measure = () => {
      // A side rail leaves no bar above the page: Wayfinder's from 980px, a test layout's from its railMinWidth.
      const besidePage = (appliedLayout === 'wayfinder' && window.innerWidth >= 980) || railActive;
      if (!minimalShellActive && besidePage) {
        root.style.setProperty('--app-header-height', '0px');
        root.style.setProperty('--topbar-viewport-height', '0px');
        setTopbarPlaceholderHeight(0);
        return;
      }
      const el = topbarRef.current;
      if (!el) return;
      const style = getComputedStyle(el);
      const mt = parseFloat(style.marginTop || '0') || 0;
      const mb = parseFloat(style.marginBottom || '0') || 0;
      const h = el.getBoundingClientRect().height;
      const full = Math.round(mt + h + mb);
      root.style.setProperty('--app-header-height', `${Math.round(h + mb)}px`);
      root.style.setProperty('--topbar-viewport-height', `${full}px`);
      setTopbarPlaceholderHeight(full);
    };

    const schedule = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(measure);
    };

    schedule();
    const onResize = () => schedule();
    window.addEventListener('resize', onResize);

    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => schedule()) : null;
    const topbar = topbarRef.current;
    if (topbar && ro) {
      ro.observe(topbar);
    }

    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener('resize', onResize);
      ro?.disconnect();
    };
  }, [appliedLayout, minimalShellActive, railActive]);

  React.useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    const selector = '.library-page .library-toolbar-block, .maintenance-layout .library-toolbar-block';
    const scrollOptions: AddEventListenerOptions = { passive: true, capture: true };
    const sentinels = new WeakMap<HTMLElement, HTMLElement>();
    // The reserve a block docked with Advanced open takes once the panel closes.
    const closedReserves = new WeakMap<HTMLElement, number>();
    const root = document.documentElement;
    let raf = 0;

    // The docking line for a toolbar in `column`: the top bar's bottom less 1px, or 0 once the bar
    // has scrolled away. A bar beside the column, like Wayfinder's side rail from 1200 px, is not
    // above it, so there the line is the window's top.
    const getMeltTop = (bar: DOMRect | null, column: DOMRect) => {
      if (!bar || bar.right <= column.left || bar.left >= column.right) return 0;
      return Math.max(0, bar.bottom - 1);
    };
    const setDockVar = (block: HTMLElement, name: string, value: string) => {
      if (block.style.getPropertyValue(name) !== value) block.style.setProperty(name, value);
    };

    const toolbarBlocks = new Set(document.querySelectorAll<HTMLElement>(selector));
    const registerToolbarTree = (element: Element) => {
      if (element instanceof HTMLElement && element.matches(selector)) toolbarBlocks.add(element);
      element.querySelectorAll<HTMLElement>(selector).forEach(block => toolbarBlocks.add(block));
    };
    const updateToolbarMelt = () => {
      raf = 0;
      const topbar = topbarRef.current;
      const bar = topbar?.getBoundingClientRect() ?? null;
      // The same line for the strips fixed under the top bar (record melt bars, StickyActionBar's
      // strip, the settings nav; global.css): 1px under the bar as it is now, so they follow it when
      // it collapses below the mobile breakpoint. Only where the bar is fixed to the window:
      // Wayfinder and Solstice scroll it away with the page, and keep their own rules there.
      // A rail fixed down the window's side (a test layout's) has nothing under it to dock to: the line is the top.
      // Tall and no wider than 400px: Focus's 64px icon rail and Field's 272px route rail. Wayfinder's rail
      // is sticky, not fixed, so it never gets here.
      const sideRail = !!bar && bar.width <= 400 && bar.height > window.innerHeight * 0.6
        && !!topbar && getComputedStyle(topbar).position === 'fixed';
      if (topbar && bar && sideRail) {
        setDockVar(root, '--topbar-dock-top', '0px');
      } else if (topbar && bar && getComputedStyle(topbar).position === 'fixed') {
        setDockVar(root, '--topbar-dock-top', `${Math.max(0, bar.bottom - 1)}px`);
      } else {
        root.style.removeProperty('--topbar-dock-top');
      }
      toolbarBlocks.forEach((block) => {
        // FullCalendar caches hit geometry at pointerdown. Thaw after drop.
        if (block.closest('[data-schedule-gesture]')) return;
        if (!block.isConnected || !block.matches(selector)) { toolbarBlocks.delete(block); return; }
        // Sticky toolbars retain their own layout space. Only change their
        // surface treatment at the boundary, never insert a spacer beside them.
        if (block.dataset.meltPosition === 'sticky') {
          const meltTop = getMeltTop(bar, block.getBoundingClientRect());
          block.closest<HTMLElement>('.library-page')?.style.setProperty('--schedule-melt-top', `${meltTop}px`);
          const dockingMedia = block.dataset.meltMedia;
          const docked = (!dockingMedia || window.matchMedia(dockingMedia).matches)
            && block.getBoundingClientRect().top <= meltTop + 1;
          if (docked) block.setAttribute('data-melted', 'true');
          else block.removeAttribute('data-melted');
          return;
        }
        // Stacked Maintenance filters can occupy most of a small viewport.
        // Keep them in document flow so expanded editors remain reachable.
        if (block.closest('.maintenance-layout') && window.innerWidth <= 1000) {
          block.removeAttribute('data-melted');
          const existingSentinel = sentinels.get(block);
          if (existingSentinel) existingSentinel.style.height = '0';
          return;
        }
        let sentinel = sentinels.get(block);
        if (!sentinel || sentinel.parentElement !== block.parentElement) {
          sentinel = document.createElement('span');
          sentinel.className = 'library-toolbar-melt-sentinel';
          sentinel.setAttribute('aria-hidden', 'true');
          sentinel.style.display = 'block';
          sentinel.style.height = '0';
          sentinel.style.margin = '0';
          sentinel.style.padding = '0';
          sentinel.style.overflow = 'hidden';
          block.parentElement?.insertBefore(sentinel, block);
          sentinels.set(block, sentinel);
        }
        // Tall toolbars can keep their normal scroll flow on smaller screens.
        const dockingMedia = block.dataset.meltMedia;
        // The sentinel holds the block's place, so it gives the block's column even once docked.
        const column = sentinel.getBoundingClientRect();
        const meltTop = getMeltTop(bar, column);
        // The line and the column, for layers that dock with the block: Wayfinder's docked
        // toolbar (global.css) and the Installations table headings.
        const page = block.closest<HTMLElement>('.library-page, .maintenance-layout') ?? block;
        setDockVar(page, '--library-melt-top', `${meltTop}px`);
        setDockVar(page, '--library-melt-left', `${column.left}px`);
        setDockVar(page, '--library-melt-right', `${Math.max(0, document.documentElement.clientWidth - column.right)}px`);
        const shouldMelt = (!dockingMedia || window.matchMedia(dockingMedia).matches)
          && column.top <= meltTop + 1;
        if (shouldMelt) {
          const advanced = block.querySelector<HTMLElement>('.library-toolbar__advanced');
          if (block.getAttribute('data-melted') !== 'true') {
            const reserveHeight = Math.ceil(block.getBoundingClientRect().height);
            // Docked, an open Advanced panel drops over the page instead of pushing it down, so the
            // reserve keeps its share only until it closes; after that the share would sit blank
            // under the toolbar. A closed panel keeps only its borders: its clientHeight is the share.
            const panelHeight = advanced?.clientHeight ?? 0;
            sentinel.style.height = `${Math.max(0, reserveHeight)}px`;
            if (panelHeight > 0) closedReserves.set(block, reserveHeight - panelHeight);
            else closedReserves.delete(block);
          } else if (advanced?.dataset.open !== 'true') {
            const closedReserve = closedReserves.get(block);
            if (closedReserve !== undefined) {
              sentinel.style.height = `${Math.max(0, closedReserve)}px`;
              closedReserves.delete(block);
            }
          }
          block.setAttribute('data-melted', 'true');
        } else {
          sentinel.style.height = '0';
          block.removeAttribute('data-melted');
        }
      });
    };

    const scheduleToolbarMelt = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(updateToolbarMelt);
    };

    const scrollTargets = new Set<EventTarget>([window, document]);
    if (document.body) {
      scrollTargets.add(document.body);
    }
    const appShell = document.querySelector('.app-shell');
    if (appShell) {
      scrollTargets.add(appShell);
    }

    scrollTargets.forEach((target) => {
      target.addEventListener('scroll', scheduleToolbarMelt, scrollOptions);
    });
    window.addEventListener('resize', scheduleToolbarMelt);
    // Below the mobile breakpoint the top bar collapses once the page scrolls (data-collapsed,
    // with a margin transition), after the scroll event that measured it. Re-measure the docking
    // line when the attribute flips (observed below) and when the bar's transition ends, or docked
    // layers keep the expanded bar's bottom until the next scroll.
    const onTopbarTransitionEnd = (event: Event) => {
      if (event.target instanceof Element && event.target.classList.contains('topbar')) scheduleToolbarMelt();
    };
    document.addEventListener('transitionend', onTopbarTransitionEnd);

    const observer = typeof MutationObserver !== 'undefined'
      ? new MutationObserver(records => {
        for (const record of records) {
          if (record.type === 'attributes' && record.target instanceof Element) registerToolbarTree(record.target);
          record.addedNodes.forEach(node => { if (node instanceof Element) registerToolbarTree(node); });
        }
        scheduleToolbarMelt();
      })
      : null;
    if (document.body && observer) {
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'data-open', 'data-collapsed'],
      });
    }

    scheduleToolbarMelt();

    return () => {
      if (raf) window.cancelAnimationFrame(raf);
      scrollTargets.forEach((target) => {
        target.removeEventListener('scroll', scheduleToolbarMelt, scrollOptions);
      });
      window.removeEventListener('resize', scheduleToolbarMelt);
      document.removeEventListener('transitionend', onTopbarTransitionEnd);
      observer?.disconnect();
      const blocks = Array.from(document.querySelectorAll(selector)) as HTMLElement[];
      blocks.forEach((block) => block.removeAttribute('data-melted'));
      document.querySelectorAll('.library-toolbar-melt-sentinel').forEach((sentinel) => sentinel.remove());
    };
  }, [loc.pathname, appliedLayout]);

  const holidayTheme = React.useMemo<ShellHolidayTheme | null>(() => (
    scheduledHoliday && isAvailableHolidayTheme(scheduledHoliday.themeKey)
      ? { id: scheduledHoliday.id, key: scheduledHoliday.themeKey, label: holidayThemeLabel(scheduledHoliday.themeKey), endsAt: scheduledHoliday.endsAt }
      : null
  ), [scheduledHoliday]);
  const profileMenuValue = React.useMemo<ProfileMenuContextValue>(
    () => ({
      accountName,
      session,
      profile,
      locationSummary,
      homeSiteLabel,
      activeLocationLabel,
      homeLocationOptions,
      homeLocationOptionsLoading,
      regionOptions,
      homeSiteDraft,
      setHomeSiteDraft,
      homeSiteBusy,
      homeSiteStatus,
      homeSiteError: combinedHomeSiteError,
      updateHomeSite,
      colorTheme,
      setColorTheme: handleColorThemePreference,
      appearanceMode,
      setAppearanceMode: handleAppearanceModePreference,
      appearanceTone,
      holidayThemeOn,
      setHolidayThemeOn: handleHolidayThemeOn,
      holidayTheme,
      holidayThemeShowing: shellTheme.holiday !== null,
      layoutStyle,
      setLayoutStyle: handleLayoutStylePreference,
      orderViewPreference,
      setOrderViewPreference: handleOrderViewPreference,
      orderChatPositionPreference,
      setOrderChatPositionPreference: handleOrderChatPositionPreference,
      colorThemeOptions: COLOR_THEME_OPTIONS,
      layoutOptions: LAYOUT_OPTIONS,
      layoutDescriptions: LAYOUT_DESCRIPTIONS,
      testLayoutOptions: isAdmin ? TEST_LAYOUT_OPTIONS : NO_TEST_LAYOUTS,
      orderViewOptions: ORDER_VIEW_OPTIONS,
      orderChatPositionOptions: ORDER_CHAT_POSITION_OPTIONS,
      userPrefsLoaded: prefsLoaded,
      logout,
    }),
    [
      accountName,
      session,
      profile,
      locationSummary,
      homeSiteLabel,
      activeLocationLabel,
      homeLocationOptions,
      homeLocationOptionsLoading,
      regionOptions,
      homeSiteDraft,
      homeSiteBusy,
      homeSiteStatus,
      combinedHomeSiteError,
      updateHomeSite,
      colorTheme,
      handleColorThemePreference,
      appearanceMode,
      handleAppearanceModePreference,
      appearanceTone,
      holidayThemeOn,
      handleHolidayThemeOn,
      holidayTheme,
      shellTheme.holiday,
      layoutStyle,
      handleLayoutStylePreference,
      orderViewPreference,
      handleOrderViewPreference,
      orderChatPositionPreference,
      handleOrderChatPositionPreference,
      prefsLoaded,
      logout,
      isAdmin,
    ],
  );
  const holidayTone = shellTheme.holiday ? shellTheme.tone : null;
  const holidayBarTone = holidayTone;
  const logoDark = appearanceTone === 'dark';
  const topbarLogoSrc = logoDark ? '/ita-logo-dark-theme.png' : '/ita-logo-vA.png';
  // A holiday's header art is drawn for a horizontal bar. Beside Focus's 64px rail, the command strip above the
  // page is that bar, so it carries the art and the rail keeps only the logo's mark (docs/holiday-themes.md).
  const holidayDecorInStrip = Boolean(session && railActive && shellCaps?.commandStrip && paletteEnabled);
  const holidayDecor = shellTheme.holiday && holidayTone && holidayBarTone ? (
    <OptionalDecorationBoundary key={shellTheme.holiday} decoration="holiday_topbar">
      <React.Suspense fallback={null}>
        <HolidayTopbarDecor holiday={shellTheme.holiday} tone={holidayTone} barTone={holidayBarTone} />
      </React.Suspense>
    </OptionalDecorationBoundary>
  ) : null;
  const holidayLogoMark = shellTheme.holiday ? (
    <OptionalDecorationBoundary key={shellTheme.holiday} decoration="holiday_logo">
      <React.Suspense fallback={null}>
        <HolidayLogoMark holiday={shellTheme.holiday} logoDark={logoDark} />
      </React.Suspense>
    </OptionalDecorationBoundary>
  ) : null;

  if (minimalShellActive) {
    return (
      <ProfileMenuContext.Provider value={profileMenuValue}>
        <ShellLayoutContext.Provider value={PUBLIC_SHELL_LAYOUT}>
        <ToastProvider>
          <div
            className="app-shell flex flex-col min-h-screen"
            data-client-portal-shell={isMinimalPortalRoute ? 'true' : undefined}
            data-site-access-blocked={siteAccess.isBlocked ? 'true' : undefined}
          >
            <DiagBoot />
            <MessageGuard allowOrigins={[]} />
            <header className="topbar topbar--viewport-fixed" ref={topbarRef} data-collapsed="true">
              {holidayDecor}
              <span className="logo-link" aria-label="ITA Venue CRM">
                <img
                  src={topbarLogoSrc}
                  alt="ITA Venue CRM"
                  className="logo-image"
                  onError={(event) => {
                    const target = event.currentTarget;
                    if (target.src.endsWith('/ita-logo-vA.png')) return;
                    target.src = '/ita-logo-vA.png';
                  }}
                />
                {holidayLogoMark}
              </span>
              {session ? (
                <div className="topbar-actions">
                  <button
                    type="button"
                    className="btn subtle topbar-actions__signout"
                    data-size="xs"
                    onClick={() => {
                      void logout();
                    }}
                  >
                    Sign out
                  </button>
                </div>
              ) : null}
            </header>
            {topbarPlaceholderHeight > 0 ? (
              <div aria-hidden="true" className="topbar-placeholder" style={{ height: topbarPlaceholderHeight }} />
            ) : null}
            <main className="content flex-1">{children}</main>
          </div>
        </ToastProvider>
        </ShellLayoutContext.Provider>
      </ProfileMenuContext.Provider>
    );
  }

  return (
    <ProfileMenuContext.Provider value={profileMenuValue}>
      <ShellLayoutContext.Provider value={shellLayoutValue}>
      <ToastProvider>
      <div className="app-shell flex flex-col min-h-screen">
      <DiagBoot />
      <MessageGuard
        allowOrigins={[
          // Keep self; add others if absolutely needed:
          // 'https://hxxrrzarvorbaonjesbj.supabase.co'
        ]}
      />
      <AppUpdateBanner />
      <header className="topbar topbar--viewport-fixed" ref={topbarRef} data-collapsed={collapsed ? 'true' : 'false'}>
        {holidayDecorInStrip ? null : holidayDecor}
        <Link to="/" className="logo-link" aria-label="Go to home">
          <img
            src={topbarLogoSrc}
            alt="ITA Venue CRM"
            className="logo-image"
            onError={(event) => {
              const target = event.currentTarget;
              if (target.src.endsWith('/ita-logo-vA.png')) return;
              target.src = '/ita-logo-vA.png';
            }}
          />
          {holidayLogoMark}
          <span className="logo-beta-chip">Beta</span>
        </Link>
        {LayoutRouteTitle && activeRoute ? (
          <LayoutFeature name="breadcrumb" className="shell-route-title">
            <LayoutRouteTitle number={activeRouteNumber} section={activeRoute.label} />
          </LayoutFeature>
        ) : null}

        {session && (
          <>
            {isMobile &&
              menuOpen &&
              typeof document !== 'undefined' &&
              createPortal(
                <div
                  className="topbar-mobile-nav-scrim"
                  aria-hidden="true"
                  onClick={() => setMenuOpen(false)}
                />,
                document.body,
              )}
            {/* The main menu on phones and tablets drops from the bar exactly as Notifications does (a
                .dropdown-menu card inside the bar, its scrim under the bar) and lists the desktop menu's own
                items: the same pages, order and current-page pill. Its links sit in a div, not a <nav>: the
                bar styles every <nav> inside it as the old dropdown nav. */}
            {isMobile && menuOpen && (
              <div
                ref={mobileNavPanelRef}
                id={navId}
                className="topbar-mobile-nav-menu dropdown-menu"
                role="dialog"
                aria-label="Main menu"
                aria-modal="true"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="dropdown-menu__header topbar-mobile-nav-menu__header">
                  <div className="dropdown-menu__title">{shellCaps?.menuLabel ?? 'Menu'}</div>
                  <button
                    type="button"
                    className="btn subtle topbar-mobile-nav-menu__close"
                    aria-label="Close menu"
                    onClick={() => setMenuOpen(false)}
                  >
                    ×
                  </button>
                </div>
                <div
                  className="topbar-mobile-nav-menu__links"
                  role="navigation"
                  aria-label="Primary navigation"
                  onClick={() => setMenuOpen(false)}
                >
                  {railNavItems.map((item) => (
                    <Link
                      key={item.key}
                      to={item.to}
                      className={item.active ? 'active' : undefined}
                      aria-current={item.active ? 'page' : undefined}
                    >
                      {item.icon ? <span className="topbar-mobile-nav-menu__icon" aria-hidden="true">{item.icon}</span> : null}
                      {item.label}
                    </Link>
                  ))}
                </div>
              </div>
            )}
            {!isMobile && menuOpen && (
              <button
                type="button"
                className="topbar-nav-overlay"
                aria-label="Close navigation"
                onClick={() => setMenuOpen(false)}
              />
            )}
            {navInBar && (
              <AdaptivePrimaryNav id={navId} items={railNavItems} showAll={!!shellCaps?.navScrolls} />
            )}
          </>
        )}

        {session ? (
          <>
            <div className="topbar-actions">
              <button
                className="menu-toggle topbar-actions__menu-button"
                aria-label={shellCaps?.menuLabel ? undefined : menuOpen ? 'Close navigation' : 'Open navigation'}
                aria-controls={navId}
                aria-expanded={menuOpen}
                onClick={() => setMenuOpen((o) => !o)}
                type="button"
              >
                <span className="menu-toggle__icon" aria-hidden="true">
                  <span />
                </span>
                {/* A layout that names its menu (Field's "Routes") shows the word, which is then the button's name. */}
                {shellCaps?.menuLabel ? <span className="menu-toggle__label">{shellCaps.menuLabel}</span> : null}
              </button>
              {(shellCaps?.commandField || classicSearch) && paletteEnabled ? (
                <React.Suspense fallback={null}>
                  <CommandStrip onOpen={() => setPaletteOpen(true)} shortcutLabel={COMMAND_SHORTCUT_LABEL} placement="bar" variant={classicSearch ? 'classic' : undefined} />
                </React.Suspense>
              ) : null}
              <React.Suspense fallback={null}>
                <NotificationBell />
              </React.Suspense>
              {isMobile ? (
                <button
                  type="button"
                  className={`topbar-actions__account-icon${profile?.headshot_url ? ' topbar-actions__account-icon--photo' : ''}`}
                  aria-label="Account and location"
                  aria-expanded={accountPanelOpen}
                  aria-controls="mobile-account-panel"
                  onClick={() => setAccountPanelOpen((o) => !o)}
                >
                  <span className="topbar-actions__account-icon-svg" aria-hidden="true">
                    {profile?.headshot_url ? (
                      <UserAvatar name={accountName} src={profile.headshot_url} size={24} />
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle className="topbar-actions__account-icon-head" cx="12" cy="8" r="4" />
                        <path d="M4 20c0-4 4-6 8-6s8 2 8 6" />
                      </svg>
                    )}
                  </span>
                  <span className="topbar-actions__account-icon-status">
                    <SimpleInOutHeaderStatus />
                  </span>
                </button>
              ) : (
                <div className="topbar-actions__profile" ref={desktopAccountMenuRef}>
                  <button
                    type="button"
                    className="menu-button topbar-actions__account-button"
                    aria-label={`Account and location for ${accountName}`}
                    aria-expanded={accountPanelOpen}
                    aria-controls="desktop-account-menu"
                    onClick={() => setAccountPanelOpen((open) => !open)}
                  >
                    <UserAvatar name={accountName} src={profile?.headshot_url} size={24} />
                    <span className="topbar-actions__profile-name">{accountFirstName}</span>
                    <span className="topbar-actions__simple-inout-status-slot">
                      <SimpleInOutHeaderStatus />
                    </span>
                    <span className="topbar-actions__account-caret" aria-hidden="true">▾</span>
                  </button>
                  {accountPanelOpen && (
                    <div
                      id="desktop-account-menu"
                      className="topbar-account-menu"
                      role="dialog"
                      aria-label="Account and location"
                    >
                      <div className="topbar-account-menu__header">
                        <span className="topbar-account-menu__name">{accountName}</span>
                        <span className="topbar-actions__simple-inout-status-slot">
                          <SimpleInOutHeaderStatus />
                        </span>
                      </div>
                      <SimpleInOutAccountMenuSection onLeave={() => setAccountPanelOpen(false)} />
                      <div className="topbar-account-menu__field">
                        <label htmlFor={`${locationSelectId}-desktop`} className="topbar-account-menu__label">
                          Location
                        </label>
                        <select
                          id={`${locationSelectId}-desktop`}
                          className="topbar-account-menu__select"
                          value={topbarSelectValue}
                          onChange={(event) => handleTopbarLocationChange(event.target.value)}
                          disabled={homeLocationOptionsLoading || homeLocationOptions.length === 0}
                          aria-label="Active location"
                          title={activeLocationLabel}
                        >
                          {homeLocationOptionsLoading ? (
                            <option value="">Loading locations…</option>
                          ) : homeLocationOptions.length === 0 ? (
                            <option value="">No accessible locations</option>
                          ) : (
                            <>
                              {(isAdmin || isManager) && <option value="">All accessible locations</option>}
                              {regionOptions.filter((r) => r.is_active && r.grant_all_locations && r.name).map((r) => (
                                <option key={`region:${r.region_id}`} value={`region:${r.region_id}`}>
                                  {r.name} (Region)
                                </option>
                              ))}
                              {homeLocationOptions.map((option) => (
                                <option key={option.location_id} value={option.location_id}>
                                  {option.name}
                                </option>
                              ))}
                            </>
                          )}
                        </select>
                      </div>
                      <Link
                        to={securityLevel === 'guest' ? '/account/security' : '/profile/edit'}
                        className="topbar-account-menu__profile-link"
                        onClick={() => setAccountPanelOpen(false)}
                      >
                        {securityLevel === 'guest' ? 'Account security' : 'Edit profile'}
                      </Link>
                      {canPreviewAccess && impersonatedRoles && impersonatedRoles.length > 0 && (
                        <div className="topbar-account-menu__impersonation">
                          <span>Impersonating {impersonatedRoles[0]}</span>
                          <button
                            className="btn micro subtle"
                            onClick={() => setImpersonatedRoles(null)}
                            title="Clear impersonation"
                            aria-label="Clear impersonation"
                            type="button"
                          >
                            &times;
                          </button>
                        </div>
                      )}
                      <button
                        type="button"
                        className="btn danger topbar-account-menu__signout"
                        onClick={() => {
                          setAccountPanelOpen(false);
                          void logout();
                        }}
                      >
                        Sign out
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
            {isMobile &&
              accountPanelOpen &&
              typeof document !== 'undefined' &&
              createPortal(
                <div className="topbar-mobile-account-portal">
                  <div
                    className="topbar-mobile-account-overlay"
                    aria-hidden="true"
                    onClick={() => setAccountPanelOpen(false)}
                  />
                  <div
                    ref={mobileAccountPanelRef}
                    id="mobile-account-panel"
                    className="topbar-mobile-account-panel"
                    role="dialog"
                    aria-label="Account and location"
                    aria-modal="true"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="topbar-mobile-account-panel__handle" aria-hidden="true" />
                    <div className="topbar-mobile-account-panel__header">
                      <h2 className="topbar-mobile-account-panel__title">Account</h2>
                      <button
                        type="button"
                        className="topbar-mobile-account-panel__close"
                        aria-label="Close"
                        onClick={() => setAccountPanelOpen(false)}
                      >
                        Done
                      </button>
                    </div>
                    <div className="topbar-mobile-account-panel__body">
                      <div className="topbar-mobile-account-panel__field">
                        <label htmlFor={`${locationSelectId}-panel`} className="topbar-mobile-account-panel__label">
                          Location
                        </label>
                        <select
                          id={`${locationSelectId}-panel`}
                          className="topbar-mobile-account-panel__select"
                          value={topbarSelectValue}
                          onChange={(event) => handleTopbarLocationChange(event.target.value)}
                          disabled={homeLocationOptionsLoading || homeLocationOptions.length === 0}
                          aria-label="Active location"
                        >
                          {homeLocationOptionsLoading ? (
                            <option value="">Loading locations…</option>
                          ) : homeLocationOptions.length === 0 ? (
                            <option value="">No accessible locations</option>
                          ) : (
                            <>
                              {(isAdmin || isManager) && <option value="">All accessible locations</option>}
                              {regionOptions.filter((r) => r.is_active && r.grant_all_locations && r.name).map((r) => (
                                <option key={`region:${r.region_id}`} value={`region:${r.region_id}`}>
                                  {r.name} (Region)
                                </option>
                              ))}
                              {homeLocationOptions.map((option) => (
                                <option key={option.location_id} value={option.location_id}>
                                  {option.name}
                                </option>
                              ))}
                            </>
                          )}
                        </select>
                      </div>
                      <div className="topbar-mobile-account-panel__field">
                        <Link
                          to="/profile/edit"
                          className="topbar-mobile-account-panel__profile-link"
                          onClick={() => setAccountPanelOpen(false)}
                        >
                          <span className="topbar-mobile-account-panel__profile-heading">
                            <span className="topbar-mobile-account-panel__profile-name">{accountName}</span>
                            <span className="topbar-actions__simple-inout-status-slot">
                              <SimpleInOutHeaderStatus />
                            </span>
                          </span>
                          <span className="topbar-mobile-account-panel__profile-hint">Edit profile</span>
                        </Link>
                      </div>
                      <div className="topbar-mobile-account-panel__field">
                        <button
                          type="button"
                          className="btn danger topbar-mobile-account-panel__signout"
                          onClick={() => {
                            setAccountPanelOpen(false);
                            void logout();
                          }}
                        >
                          Sign out
                        </button>
                      </div>
                      {canPreviewAccess && impersonatedRoles && impersonatedRoles.length > 0 && (
                        <div className="topbar-mobile-account-panel__impersonation">
                          <span className="pill topbar-actions__impersonation">
                            Impersonating {impersonatedRoles[0]}
                            <button
                              className="btn micro subtle"
                              onClick={() => setImpersonatedRoles(null)}
                              title="Clear impersonation"
                              aria-label="Clear impersonation"
                              type="button"
                            >
                              &times;
                            </button>
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>,
                document.body,
              )}
          </>
        ) : null}
      </header>
      {topbarPlaceholderHeight > 0 ? (
        <div aria-hidden="true" className="topbar-placeholder" style={{ height: topbarPlaceholderHeight }} />
      ) : null}

      {/* Dev-only focus stability guard */}
      <FocusRemountGuard />
      {/* Deep research approvals moved to NotificationBell dropdown */}
      <div className="app-main-area">
        {session && shellCaps?.commandStrip && paletteEnabled ? (
          <React.Suspense fallback={null}>
            <CommandStrip onOpen={() => setPaletteOpen(true)} shortcutLabel={COMMAND_SHORTCUT_LABEL}>
              {holidayDecorInStrip ? holidayDecor : null}
            </CommandStrip>
          </React.Suspense>
        ) : null}
        {LayoutBreadcrumb && activeRoute ? (
          <LayoutFeature name="breadcrumb" className="shell-breadcrumb">
            <ShellBreadcrumb Breadcrumb={LayoutBreadcrumb} number={activeRouteNumber} section={activeRoute.label} sectionTo={activeRoute.to} pathname={loc.pathname} />
          </LayoutFeature>
        ) : null}
        <main className="content flex-1">{children}</main>
        <Footer
          showItemsDebugToggle={isSuperAdmin}
          itemsDebugEnabled={itemsDebugEnabled}
          onToggleItemsDebug={() => setItemsDebugEnabled((prev) => !prev)}
        />
      </div>
      {session && (
        <React.Suspense fallback={null}>
          <DeferMount idle delay={600}>
            <ChatWidget />
          </DeferMount>
        </React.Suspense>
      )}
      {session && (
        <React.Suspense fallback={null}>
          <DeferMount idle delay={750}>
            <OrderChatMentionOverlay />
          </DeferMount>
        </React.Suspense>
      )}
      {session ? <SimpleInOutOverlays /> : null}
      <SuperAdminHoverInspector />
      {session && bottomNavItems.length > 0 ? (
        <React.Suspense fallback={null}>
          <ShellBottomNav items={bottomNavItems} />
        </React.Suspense>
      ) : null}
      {session && paletteOpen ? (
        <React.Suspense fallback={null}>
          <CommandPalette items={paletteItems} onClose={() => setPaletteOpen(false)} search={paletteSearch} searchMinLength={RECORD_SEARCH_MIN_LENGTH} />
        </React.Suspense>
      ) : null}
      </div>
      </ToastProvider>
      </ShellLayoutContext.Provider>
    </ProfileMenuContext.Provider>
  );
}
